import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Clock, Instagram, MessageCircle, Truck } from "lucide-react";
import { useLanguage } from "@/i18n";
import { AccentText, Reveal } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const MAPS_URL = "https://www.google.com/maps/place/MIDORA+Atelier+Gastronomico/@45.482652,9.2471305,17z";

const inputCls =
    "w-full border-b border-white/15 bg-transparent py-3 text-sm text-white outline-none transition-colors duration-300 placeholder:text-white/25 focus:border-midora-red";
const labelCls = "mb-1 block font-mono text-[10px] uppercase tracking-[0.25em] text-white/45";

export const ContactSection = () => {
    const { lang, t } = useLanguage();
    const [form, setForm] = useState({ business_name: "", business_type: "", vat_number: "", email: "", phone: "", message: "", website: "" });
    const [sending, setSending] = useState(false);
    const [privacy, setPrivacy] = useState(false);

    const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });

    const submit = async (e) => {
        e.preventDefault();
        if (!form.business_name || !form.business_type || !form.vat_number || !form.email) {
            toast.error(t("contact.invalid"));
            return;
        }
        if (!privacy) {
            toast.error(t("contact.privacyError"));
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/listino-requests`, { ...form, lang });
            toast.success(t("contact.success"));
            setForm({ business_name: "", business_type: "", vat_number: "", email: "", phone: "", message: "", website: "" });
        } catch {
            toast.error(t("contact.error"));
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contatti" data-testid="contact-section" className="mx-auto max-w-[1600px] px-5 py-24 md:px-12 md:py-32 lg:px-24">
            <div className="grid gap-16 lg:grid-cols-2 lg:gap-20">
                <div>
                    <Reveal>
                        <p className="mb-4 font-mono text-[11px] uppercase tracking-[0.3em] text-midora-red" data-testid="contact-overline">
                            {t("contact.overline")}
                        </p>
                        <h2 className="font-display text-4xl font-light tracking-tighter sm:text-5xl" data-testid="contact-heading">
                            <AccentText text={t("contact.heading")} accentKey="contact" />
                        </h2>
                        <p className="mt-4 max-w-md text-sm italic leading-relaxed text-midora-red md:text-base" data-testid="contact-sub">
                            {t("contact.sub")}
                        </p>
                    </Reveal>

                    <Reveal delay={0.1}>
                        <div className="mt-12 space-y-8">
                            <div data-testid="contact-info-service">
                                <p className={labelCls}>{t("contact.service")}</p>
                                <a href="tel:+393393506050" data-testid="contact-phone-link" className="flex items-center gap-3 text-lg text-white transition-colors duration-300 hover:text-midora-red">
                                    <Phone className="h-4 w-4 text-midora-red" /> +39 339 350 6050
                                </a>
                                <a href="mailto:info@midoraatelier.com" data-testid="contact-email-link" className="mt-2 flex items-center gap-3 text-white/70 transition-colors duration-300 hover:text-midora-red">
                                    <Mail className="h-4 w-4 text-midora-red" /> info@midoraatelier.com
                                </a>
                            </div>

                            <div data-testid="contact-info-warehouse">
                                <p className={labelCls}>{t("contact.warehouse")}</p>
                                <a href={MAPS_URL} target="_blank" rel="noopener noreferrer" data-testid="contact-maps-link" className="flex items-start gap-3 text-white/70 transition-colors duration-300 hover:text-midora-red">
                                    <MapPin className="mt-1 h-4 w-4 shrink-0 text-midora-red" />
                                    <span>
                                        Via Privata Oslavia 17/3 C
                                        <br />
                                        <span className="text-white/50">{t("contact.city")}</span>
                                        <span className="mt-1 block font-mono text-[10px] uppercase tracking-[0.2em] text-midora-red">{t("contact.maps")}</span>
                                    </span>
                                </a>
                                <p className="mt-3 flex items-center gap-3 text-sm text-white/50">
                                    <Clock className="h-4 w-4 text-midora-red" /> {t("contact.hours")}
                                </p>
                            </div>

                            <div className="border-t border-white/10 pt-8" data-testid="contact-delivery">
                                <p className={labelCls}>
                                    <Truck className="mr-2 inline h-4 w-4 text-midora-red" />
                                    {t("contact.deliveryTitle")}
                                </p>
                                <p className="text-sm leading-relaxed text-white/55">{t("contact.deliveryText")}</p>
                            </div>

                            <div className="flex flex-wrap gap-3">
                                <a
                                    data-testid="contact-whatsapp-btn"
                                    href="https://wa.me/393393506050"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-midora-red hover:text-midora-red"
                                >
                                    <MessageCircle className="h-4 w-4" /> {t("contact.whatsapp")}
                                </a>
                                <a
                                    data-testid="contact-instagram-btn"
                                    href="https://www.instagram.com/midoraateliergastronomico"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex items-center gap-2 border border-white/20 px-5 py-3 font-mono text-[10px] uppercase tracking-[0.2em] text-white transition-colors duration-300 hover:border-midora-red hover:text-midora-red"
                                >
                                    <Instagram className="h-4 w-4" /> Instagram
                                </a>
                            </div>
                        </div>
                    </Reveal>
                </div>

                <Reveal delay={0.15}>
                    <form data-testid="listino-form" onSubmit={submit} className="border border-white/10 bg-[#151921] p-7 md:p-10">
                        <div className="mb-8">
                            <span className="font-mono text-[10px] uppercase tracking-[0.3em] text-midora-red">B2B — Ho.Re.Ca.</span>
                            <h3 className="mt-2 font-display text-2xl font-light italic tracking-tight">{t("contact.heading")}</h3>
                        </div>

                        <div className="grid gap-6 sm:grid-cols-2">
                            <div className="sm:col-span-2">
                                <label className={labelCls} htmlFor="lf-business">{t("contact.business")} *</label>
                                <input id="lf-business" data-testid="listino-input-business" className={inputCls} value={form.business_name} onChange={set("business_name")} required maxLength={120} />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="lf-type">{t("contact.type")} *</label>
                                <select id="lf-type" data-testid="listino-select-type" className={`${inputCls} cursor-pointer bg-[#151921]`} value={form.business_type} onChange={set("business_type")} required>
                                    <option value="" disabled className="bg-[#232833]">—</option>
                                    {Object.entries(t("contact.types")).map(([value, label]) => (
                                        <option key={value} value={value} className="bg-[#232833]">
                                            {label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="lf-vat">{t("contact.vat")} *</label>
                                <input id="lf-vat" data-testid="listino-input-vat" className={inputCls} value={form.vat_number} onChange={set("vat_number")} required maxLength={16} />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="lf-email">{t("contact.email")} *</label>
                                <input id="lf-email" type="email" data-testid="listino-input-email" className={inputCls} value={form.email} onChange={set("email")} required />
                            </div>
                            <div>
                                <label className={labelCls} htmlFor="lf-phone">{t("contact.phone")}</label>
                                <input id="lf-phone" data-testid="listino-input-phone" className={inputCls} value={form.phone} onChange={set("phone")} maxLength={30} />
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelCls} htmlFor="lf-message">{t("contact.message")}</label>
                                <textarea id="lf-message" data-testid="listino-input-message" rows={3} className={`${inputCls} resize-none`} value={form.message} onChange={set("message")} maxLength={1000} />
                            </div>
                        </div>

                        <div className="mt-8 flex items-start gap-3">
                            <input
                                id="lf-privacy"
                                type="checkbox"
                                data-testid="listino-privacy-checkbox"
                                checked={privacy}
                                onChange={(e) => setPrivacy(e.target.checked)}
                                className="mt-1 h-4 w-4 shrink-0 cursor-pointer accent-[#A30036]"
                            />
                            <label htmlFor="lf-privacy" className="cursor-pointer text-xs leading-relaxed text-white/55">
                                {t("contact.privacyLabel")}{" "}
                                <Link to="/privacy" data-testid="listino-privacy-link" className="text-midora-red underline underline-offset-2 transition-colors duration-300 hover:text-white">
                                    {t("cookie.policy")}
                                </Link>
                            </label>
                        </div>

                        <input type="text" name="website" value={form.website} onChange={set("website")} className="hidden" tabIndex={-1} autoComplete="off" aria-hidden="true" />

                        <button
                            type="submit"
                            data-testid="listino-submit-button"
                            disabled={sending}
                            className="mt-10 w-full bg-midora-red px-8 py-4 font-mono text-[11px] uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-[#DBD1BD] hover:text-[#1C2029] disabled:opacity-50"
                        >
                            {sending ? t("contact.sending") : t("contact.submit")}
                        </button>
                    </form>
                </Reveal>
            </div>
        </section>
    );
};
